'use client';

import { useTheme } from '@emotion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { http } from '../../../../lib/api/http';
import { V2, V4 } from '../../../../lib/api/urls';
import {
  activeStatus,
  archiveStatus,
  closeStatus,
  inactiveStatus,
} from '../../../../constants/events';
import { Container } from '../../../../components/layout/Container';
import { DEVICE_SCREENS_MAX_STRING } from '../../../../lib/consts';
import { BreadCrumbs } from '../../../../components/blocks/BreadCrumbs';
import { AsideEventBlock } from '../../../../components/blocks/AsideEventBlock';
import { EventBanner } from '../../../../components/blocks/EventBanner';
import { ShareBlock } from '../../../../components/blocks/ShareBlock';
import { Link } from '../../../../components/styled/Link';
import { Image } from '../../../../components/picture/picture';
import CheckMarkSVG from '../../../../public/img/check-mark.svg';
import ServiceFreeSVG from '../../../../public/img/advantages/service_free.svg';
import LockSVG from '../../../../public/img/advantages/lock.svg';
import BankCardSVG from '../../../../public/img/advantages/bank_card.svg';
import CancelSVG from '../../../../public/img/advantages/cancel.svg';
import CircleSVG from '../../../../public/img/advantages/circle.svg';
import paymentOnSpotImg from '../../../../public/img/advantages/payment-on-spot.png';
import PushkinSVG from '../../../../public/img/pushkin.svg';
import { Description } from '../../../../components/blocks/Description';
import { LabelEvent } from '../../../../components/styled/LabelEvent';
import {
  AskQuestion,
  BestPriceCollapsable,
} from '../../../../components/collapsable/collapsable';
import { CommentsBlock } from '../../../../components/comments/CommentsBlock';
import { Subscription } from '../../../../components/about/Subscription';
import { MobileApps } from '../../../../components/about/MobileApps';
import { Viewed } from '../../../../components/viewed/viewed';
import { fetchViewed } from '../../../../utils/viewed';
import { UpButton } from '../../../../components/common/UpButton';
import { useModal } from '../../../../providers/modal';
import { ActionWidget } from '../../../../components/blocks/ActionWidget';
import { PictureModal } from '../../../../components/picture/PictureModal';
import {
  BuyButton,
  GBButton,
  RedButton,
} from '../../../../components/styled/Button';

import {
  getBuyButtonText,
  getCancellationPolicyText,
  isEventPassed,
  useSendMetricsFromButton,
} from '../../../../utils/event';
import { BookingForm } from '../../../../components/booking-form';
import { Action, CategoryData, PaymentItem } from '../../../../models/action';
import { ACTION_TYPES } from '../../../../models/action-types';
import { City, Person, Social } from '../../../../models';
import { PaymentModal } from '../../../../components/booking-form/payment-modal';
import SubscribeForm from '../../../../components/form/SubscribeForm';
import SubscribeModal from '../../../../components/subscribe/subscribe-modal';
import SubscribeModalForm from '../../../../components/form/SubscribeForm/modal';
import FireLogoSVG from '../../../../public/img/fire-logo.svg';
import wordDeclension from '../../../../utils/word-declension';
import { useAuthorization } from '../../../../providers/authorization';
import { initialModalState } from '../../../../models/modal';
import { ImagesSlider } from '../../../../components/actions/images-slider';
import {
  ModalContent,
  ModalContentWrapper,
  ModalTitleWrapper,
} from '../../../../components/common/Modal';
import { CloseSVG } from '../../../../components/styled/Close';
import GuaranteeSVG from '../../../../public/img/guarantee.svg';
import { SimilarEventsBlock } from '../../../../components/actions/SimilarEventsBlock';
import { PersonsBlock } from '../../../../components/actions/PersonalsBlock';
import { ConfigTypes } from '../../../../models/config';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../../../../lib/api/browserStorage';
import { useHistory } from '../../../../providers/history';
import { useTSNextWidget } from '../../../../hooks/useTSNextWidget';
import { useCookieAgreement } from '../../../../providers';
import { withSuspense } from '../../../../utils/withSuspense';
import { Comment } from '../../../../models/comment';
import { CategoriesBlock } from '../../../../components/blocks/CategoriesBlock';

function PageComponentInner({
  currentCity,
  eventData,
  topTicketPage,
  promos,
  socials,
  gbReccomendLink,
  configOfSite,
  commentsForMicrodata,
  performerForMicrodata,
  categoriesBlockItems,
}: {
  currentCity: City;
  eventData: Action;
  promos: { results: any };
  socials: { results: Social[] };
  gbReccomendLink: string | null;
  topTicketPage: null | {
    link: string;
    open_in_new_window: boolean;
    title: string;
    description: string;
  };
  configOfSite: ConfigTypes;
  commentsForMicrodata: Comment[];
  performerForMicrodata: Person[];
  categoriesBlockItems: CategoryData[];
}) {
  const auth = useAuthorization();
  const theme = useTheme();
  const { showPopUp } = useCookieAgreement();
  const [viewed, setViewed] = useState<{ results: Action[] }>({ results: [] });
  const [isShowBuyButton, setIsShowBuyButton] = useState(false);
  const [isBuyButtonReady, setBuyButtonReady] = useState(false);
  const modalState = useModal();
  const [imageModal, setImageModal] = useState<{
    slide: number | null;
    visible: boolean;
  }>({ slide: null, visible: false });
  const router = useRouter();
  const searchParams = useSearchParams();

  const seoAnalyticsFromButton = useSendMetricsFromButton();
  const { TSNextWidgetScript, tsNextWidgetConfig, fakeClick } = useTSNextWidget(
    {
      configOfSite,
      eventData,
    }
  );
  const history = useHistory();

  const buyButtonRef = useRef<HTMLDivElement>(null);
  const [isEditor, setIsEditor] = useState(false);

  useEffect(() => {
    if (auth.isReady) {
      if (
        auth.user?.roles &&
        auth.user.roles.find((item) => item.title === 'site_editor')
      ) {
        setIsEditor(true);
      }
    }
  }, [auth]);

  const isArchive = () => {
    if (eventData.not_season) {
      return false;
    }
    if (isEventPassed(eventData) && !eventData.date_unknown) {
      return true;
    }
    return (
      eventData.status === archiveStatus ||
      eventData.status === closeStatus ||
      eventData.status === inactiveStatus
    );
  };

  useEffect(() => {
    if (eventData.type === 'tsnext_widget') {
      setBuyButtonReady(auth.isReady);
    } else {
      setBuyButtonReady(true);
    }
  }, [auth, eventData]);

  const isBuyDisabled = isArchive();

  useEffect(() => {
    async function fetch() {
      const viewedList = await fetchViewed(eventData.id);
      setViewed(viewedList);
    }
    fetch();
    let viewedActions = [];
    viewedActions =
      (JSON.parse(getLocalStorageItem('viewed-events') || '[]') || [])
        .filter((id: number) => id !== eventData.id)
        .slice(0, 8) || [];
    setLocalStorageItem(
      'viewed-events',
      JSON.stringify([eventData.id, ...viewedActions])
    );
  }, [eventData.id]);

  useEffect(() => {
    const onScroll = () => {
      const element = buyButtonRef.current;
      const isScrolled = !!element && element.getBoundingClientRect().top < 0;
      setIsShowBuyButton(isScrolled);
    };
    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    http.post(V2.eventViews, {
      Action: eventData.remote_id,
    });
  }, [eventData.remote_id]);

  const openBestPriceDecription = () =>
    topTicketPage &&
    modalState?.setModalState((old) => ({
      ...old,
      show: true,
      content: (
        <ModalContentWrapper>
          <ModalTitleWrapper
            showClose
            setModalState={modalState?.setModalState}
          >
            <div
              css={{
                fontFamily: 'Inter',
                fontSize: '24px',
                fontWeight: 600,
              }}
            >
              {topTicketPage.title}
            </div>
          </ModalTitleWrapper>
          <ModalContent>
            <Description
              needCrop={false}
              description={topTicketPage.description}
            />
          </ModalContent>
        </ModalContentWrapper>
      ),
      options: {},
    }));

  const isRanOutOfTickets =
    eventData.event_categories
      .filter((cat) => cat.is_active)
      .filter((cat) => cat.is_unlimited || cat.quantity).length === 0;

  const order = searchParams.get('order');

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (typeof order === 'string' && eventData.not_season) {
      return () => modalState?.setModalState(initialModalState);
    }
    if (typeof order === 'string') {
      if (eventData.type === ACTION_TYPES.partner_widget) {
        return modalState?.setModalState((old) => ({
          ...old,
          show: true,
          content: (
            <ModalContentWrapper>
              <ModalTitleWrapper
                showClose
                setModalState={modalState?.setModalState}
                customClose={
                  <CloseSVG
                    onClick={() => {
                      modalState?.setModalState(initialModalState);
                      const url = new URL(window.location.href);
                      url.searchParams.delete('order');
                      router.replace(url.href);
                    }}
                    css={{ width: 20, height: 20 }}
                  />
                }
              />
              <ModalContent>
                <ActionWidget action={eventData} />
              </ModalContent>
            </ModalContentWrapper>
          ),
          options: {},
          outsideCloseCallback: () => {
            const url = new URL(window.location.href);
            url.searchParams.delete('order');
            router.replace(url.href);
          },
        }));
      }
      if (eventData.type === ACTION_TYPES.tsnext_widget) {
        fakeClick();
        return () => modalState?.setModalState(initialModalState);
      }
      modalState?.setModalState((old) => ({
        ...old,
        show: true,
        content: (
          <BookingForm
            setModalState={modalState.setModalState}
            actionData={eventData}
            gbReccomendLink={gbReccomendLink}
            currentCity={currentCity}
            banOnSomeEmails={
              !!configOfSite.find(
                (item) => item.name === 'WARN_ON_RESTRICTED_EMAIL_DOMAINS'
              )?.value
            }
            banEmails={configOfSite.find(
              (item) => item.name === 'RESTRICTED_EMAIL_DOMAINS'
            )}
          />
        ),
        options:
          eventData.type === ACTION_TYPES.purchase ? { width: '50%' } : {},
        outsideCloseCallback: () => {
          const url = new URL(window.location.href);
          url.searchParams.delete('order');
          router.replace(url.href);
        },
      }));
      return () => modalState?.setModalState(initialModalState);
    }
  }, [order, auth]);

  const paymentStatus = searchParams.get('payment_status');

  useEffect(() => {
    if (paymentStatus) {
      const reservationId = getLocalStorageItem(ACTION_TYPES.reservation);
      if (reservationId) {
        http
          .get(V4.reservationsItem(reservationId), null, null, {
            contentType: 'json',
            withTokenRequest: true,
          })
          .then((reservationResponse) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (window.dataLayer.find((item) => item.ecommerce)) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              window.dataLayer.push({ ecommerce: null });
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.dataLayer.push({
              ecommerce: {
                currencyCode: 'RUB',
                purchase: {
                  actionField: {
                    id: reservationResponse.id,
                  },
                  products: reservationResponse.payment_items.map(
                    (item: PaymentItem) => ({
                      id: reservationResponse.event.id,
                      name: reservationResponse.event.title,
                      price: item.price,
                      category: reservationResponse.event.marketing_category,
                      variant: item.name,
                      quantity: item.quantity,
                    })
                  ),
                },
              },
            });
          });
      }
      modalState?.setModalState((old) => ({
        ...old,
        show: true,
        content: (
          <PaymentModal
            gbReccomendLink={gbReccomendLink}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            status={paymentStatus}
            setModalState={modalState.setModalState}
          />
        ),
        outsideCloseCallback: () => {
          modalState.setModalState(initialModalState);
          if (gbReccomendLink && paymentStatus === 'success') {
            router.push(gbReccomendLink);
          }
        },
      }));
    }
  }, [
    paymentStatus,
    // ???
    searchParams.get('orderId'),
  ]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (window.dataLayer) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (window.dataLayer.find((item) => item.ecommerce)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.dataLayer.push({ ecommerce: null });
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.dataLayer.push({
        ecommerce: {
          currencyCode: 'RUB',
          detail: {
            products: [
              {
                id: eventData.id,
                name: eventData.title,
                price: eventData.filter_price,
                category: eventData.marketing_category,
              },
            ],
          },
        },
      });
    }
  }, [eventData]);

  function onSuccessSubscribe(email: string) {
    modalState?.setModalState((old) => ({
      ...old,
      show: true,
      content: (
        <SubscribeModal notSeason={eventData.not_season} email={email} />
      ),
    }));
  }
  function onClickBuyButton() {
    seoAnalyticsFromButton.onSend(eventData, isRanOutOfTickets);
    _tmr.push({ type: 'reachGoal', id: 3134178, goal: 'purchase' });
    const url = new URL(window.location.href);
    if (isRanOutOfTickets || eventData.not_season) {
      if (eventData.subscription_confirmed) {
        return;
      }
      const email = auth?.getUser()?.email;
      if (email) {
        http
          .post(V2.subscriptions, {
            source: eventData.not_season
              ? 'SeasonStart'
              : 'ArchiveSubscription',
            event_id: eventData.id,
            city: currentCity.remote_id,
            email: auth?.getUser()?.email,
          })
          .then(() => {
            onSuccessSubscribe(email);
          });
      } else {
        modalState?.setModalState((old) => ({
          ...old,
          show: true,
          content: (
            <SubscribeModalForm
              notSeason={eventData.not_season}
              eventId={eventData.id}
              currentCity={currentCity}
              onSuccessSubscribe={onSuccessSubscribe}
            />
          ),
        }));
      }
      return;
    }
    switch (eventData.type) {
      case ACTION_TYPES.certificate:
      case ACTION_TYPES.reservation:
      case ACTION_TYPES.purchase:
      case ACTION_TYPES.FREE:
      case ACTION_TYPES.partner_widget:
        url.searchParams.append('order', '');
        router.replace(url.href);
        break;
      default:
        break;
    }
  }
  const generateMicrodataEventStatus = (statusOfEvent: string) => {
    if (['active', 'deferred', 'hidden'].includes(statusOfEvent)) {
      return (
        <meta
          itemProp='eventStatus'
          content='https://schema.org/EventScheduled'
        />
      );
    }
    if (['inactive', 'close'].includes(statusOfEvent)) {
      return (
        <meta
          itemProp='eventStatus'
          content='https://schema.org/EventCancelled'
        />
      );
    }
    // archive
    return (
      <meta
        itemProp='eventStatus'
        content='https://schema.org/EventPostponed'
      />
    );
  };

  const createBreadCrumbs = () => {
    if (
      history &&
      history.lastPage &&
      history.lastPage.type === 'compilation'
    ) {
      return [
        {
          title: 'Подборки',
          url: 'compilations',
        },
        {
          title: history.lastPage.title || '',
          href: history.lastPage.url,
        },
        { title: eventData.title },
      ];
    }
    return [
      {
        title: 'Каталог',
        url: 'catalog',
      },
      { title: eventData.title },
    ];
  };

  return (
    <Container
      css={{
        padding: '15px 12px',
        [DEVICE_SCREENS_MAX_STRING.sm]: {
          padding: '0 !important',
        },
      }}
    >
      <div
        css={{
          marginBottom: 16,
          [DEVICE_SCREENS_MAX_STRING.lg]: {
            display: 'none',
          },
        }}
      >
        <BreadCrumbs currentCity={currentCity} items={createBreadCrumbs()} />
      </div>
      <main css={{ fontFamily: 'Golos', color: theme.colors.black }}>
        <EventBanner
          disabled={isBuyDisabled}
          isBuyButtonReady={isBuyButtonReady}
          item={eventData}
          onButtonClick={() => onClickBuyButton()}
          isRanOutOfTickets={isRanOutOfTickets}
          tsNextWidgetConfig={tsNextWidgetConfig}
          isEditor={isEditor}
        />
        {(!eventData.date_unknown || eventData.not_season) && (
          <div
            ref={buyButtonRef}
            css={{
              display: 'none',
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                display: 'block',
                width: `100%`,
                paddingRight: 32,
                paddingLeft: 32,
                position: `absolute`,
                zIndex: 2,
                button: {
                  padding: 'unset',
                  height: 50,
                  fontSize: 18,
                  borderRadius: 30,
                },
              },
            }}
          >
            <RedButton
              callback={() => onClickBuyButton()}
              disabled={isBuyDisabled || !isBuyButtonReady}
              full
              montserrat
              subscription_confirmed={
                eventData.status === activeStatus &&
                eventData.subscription_confirmed
              }
              {...(tsNextWidgetConfig &&
                !(
                  isBuyDisabled ||
                  eventData.not_season ||
                  !isBuyButtonReady
                ) && {
                  'data-type': tsNextWidgetConfig.type,
                  'data-uid': tsNextWidgetConfig.id,
                  'data-category': eventData.marketing_category,
                  'data-EXT_ID': auth.user?.external_ids.ext_id,
                  'data-ANALYTICS_ID': auth.user?.external_ids.analytical_id,
                  className: 'tsnext-widget',
                })}
            >
              {eventData.status === activeStatus &&
                eventData.subscription_confirmed && (
                  <CheckMarkSVG css={{ width: 16, height: 14 }} />
                )}
              {getBuyButtonText(eventData, isRanOutOfTickets)}
            </RedButton>
          </div>
        )}
        <div
          itemScope
          itemType='https://schema.org/Event'
          css={{
            display: 'flex',
            marginTop: 30,
            marginBottom: 50,
            [DEVICE_SCREENS_MAX_STRING.md]: {
              flexDirection: 'column',
              padding: '0 8px',
              marginTop: 0,
            },
          }}
        >
          <span css={{ display: 'none' }} itemProp='name'>
            {eventData.title}
          </span>
          {eventData.sponsor && (
            <meta itemProp='organizer' content={eventData.sponsor.title} />
          )}
          {eventData.latest_event && (
            <meta
              itemProp='endDate'
              content={eventData.latest_event.end_date}
            />
          )}
          {eventData.latest_event && (
            <meta
              itemProp='endDate'
              content={eventData.latest_event.end_date}
            />
          )}
          {performerForMicrodata.length > 0 && (
            <meta
              itemProp='performer'
              content={`${performerForMicrodata[0].first_name} ${performerForMicrodata[0].last_name}`}
            />
          )}
          {generateMicrodataEventStatus(eventData.status)}
          {eventData.images[0] && eventData.images[0].webp_360 && (
            <meta
              itemProp='image'
              content={eventData.images[0].webp_360 as string}
            />
          )}
          {!!eventData.comments_avg_rating && (
            <span
              itemProp='aggregateRating'
              itemScope
              itemType='https://schema.org/AggregateRating'
              css={{ display: 'none' }}
            >
              <span itemProp='ratingValue'>
                {String(eventData.comments_avg_rating.toFixed(1))}
              </span>
              <span itemProp='ratingCount'>
                {String(eventData.comments_with_rating_count)}
              </span>
            </span>
          )}
          {commentsForMicrodata.map((itemOfComments: Comment) =>
            !!itemOfComments.author && !!itemOfComments.rating ? (
              <div
                itemProp='review'
                itemScope
                itemType='https://schema.org/Review'
                css={{ display: 'none' }}
                key={itemOfComments.id}
              >
                <span
                  itemProp='reviewRating'
                  itemScope
                  itemType='https://schema.org/Rating'
                >
                  <span itemProp='ratingValue'>
                    {String(itemOfComments.rating)}
                  </span>
                </span>
                <span
                  itemProp='author'
                  itemScope
                  itemType='https://schema.org/Person'
                >
                  <span itemProp='name'>
                    {itemOfComments.name
                      ? itemOfComments.name
                      : `${itemOfComments.author.first_name} ${itemOfComments.author.last_name}`}
                  </span>
                </span>
                <meta
                  itemProp='datePublished'
                  content={itemOfComments.created_at}
                />
                <div itemProp='reviewBody'>{itemOfComments.description}</div>
                <span
                  itemProp='publisher'
                  itemScope
                  itemType='https://schema.org/Organization'
                >
                  <meta itemProp='name' content='ООО "Горбилет"' />
                </span>
              </div>
            ) : null
          )}
          <div
            css={{
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                marginTop: 50,
              },
            }}
            className='col-12 col-md-5 col-lg-4 col-xl-3'
          >
            {eventData.type !== 'FREE' &&
              eventData.reservation_count_text &&
              !eventData.countdown_tickets && (
                <p
                  css={{
                    marginTop: 18,
                    marginBottom: 16,
                    fontSize: 14,
                    color: '#282828',
                    fontFamily: "'Montserrat', serif",
                  }}
                >
                  {eventData.reservation_count_text}
                </p>
              )}
            {eventData.type !== 'FREE' &&
            eventData.countdown_tickets &&
            !eventData.not_season &&
            !isArchive() ? (
              <p
                css={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  marginTop: 10,
                  marginBottom: 18,
                  color: '#282828',
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    marginTop: 18,
                    marginBottom: 0,
                  },
                }}
              >
                <FireLogoSVG css={{ width: 18, height: 18 }} />
                <span
                  css={{
                    fontSize: 14,
                    fontFamily: 'Montserrat, serif',
                  }}
                >
                  {wordDeclension(
                    eventData.countdown_tickets,
                    'Остался',
                    'Осталось',
                    'Осталось'
                  )}{' '}
                  <b css={{ fontWeight: 'bolder' }}>
                    {eventData.countdown_tickets}
                  </b>{' '}
                  {wordDeclension(
                    eventData.countdown_tickets,
                    'билет',
                    'билета',
                    'билетов'
                  )}
                </span>
              </p>
            ) : (
              ''
            )}
            <div
              css={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexWrap: 'wrap',
                gap: 6,
                marginBottom: 24,
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  marginTop: 18,
                },
              }}
            >
              {eventData.not_season && (
                <LabelEvent text='Не сезон' description='Не сезон' />
              )}
              {eventData.hit && (
                <LabelEvent
                  text='хит'
                  description='Пользователи часто просматривают это мероприятие'
                />
              )}
              {eventData.premiere && (
                <LabelEvent
                  text='премьера'
                  description='Премьерные показы мероприятия'
                />
              )}
              {eventData.first_time && (
                <LabelEvent
                  text='впервые'
                  description='Событие размещено на сайте впервые'
                />
              )}
              {eventData.best_reviews && (
                <LabelEvent
                  text='лучшие отзывы'
                  description='Пользователи высоко оценивают мероприятие'
                />
              )}
              {eventData.prepayment && (
                <LabelEvent
                  text='предоплата'
                  description='Онлайн вы вносите только предоплату'
                />
              )}
            </div>
            <AsideEventBlock item={eventData} currentCity={currentCity} />
            {eventData.pushkin_card_payment && (
              <div
                css={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '1.5px solid #F44336',
                  padding: 16,
                  gap: 16,
                }}
              >
                <PushkinSVG
                  css={{
                    width: 44,
                    height: 44,
                  }}
                />
                <span
                  css={{
                    width: 220,
                    fontSize: 14,
                    fontWeight: 400,
                    color: theme.colors.text,
                  }}
                >
                  событие можно оплатить Пушкинской картой
                </span>
              </div>
            )}
            <div
              css={{
                marginTop: 20,
                textAlign: 'center',
                [DEVICE_SCREENS_MAX_STRING.sm]: {
                  display: 'none',
                },
              }}
            >
              {(!isEventPassed(eventData) || eventData.not_season) &&
                eventData.dates_txt &&
                !isRanOutOfTickets && (
                  <GBButton
                    callback={() =>
                      isArchive() || !isBuyButtonReady
                        ? null
                        : onClickBuyButton()
                    }
                    text={getBuyButtonText(eventData, isRanOutOfTickets)}
                    size='sm'
                    modification='shadow'
                    buttonStyle={{
                      width: '100%',
                      height: '40px',
                      borderRadius: '19px',
                      // width: '220px',
                      fontFamily: 'Golos',
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '16px',
                      ...(isArchive() ? { background: '#A5A2A1' } : {}),
                    }}
                    textStyle={{
                      textAlign: 'center',
                    }}
                    {...(tsNextWidgetConfig &&
                      !(
                        isBuyDisabled ||
                        eventData.not_season ||
                        !isBuyButtonReady
                      ) && {
                        'data-type': tsNextWidgetConfig.type,
                        'data-uid': tsNextWidgetConfig.id,
                        'data-category': eventData.marketing_category,
                        'data-EXT_ID': auth.user?.external_ids.ext_id,
                        'data-ANALYTICS_ID':
                          auth.user?.external_ids.analytical_id,
                        className: 'tsnext-widget',
                      })}
                  />
                )}
            </div>
            {promos.results && promos.results.length > 0 && (
              <div css={{ marginTop: 20 }}>
                <Link href={[promos.results[0].url].join('/')}>
                  <Image image={promos.results[0].image} alt='промо' />
                </Link>
              </div>
            )}
            {eventData.status === activeStatus &&
              isRanOutOfTickets &&
              (eventData.subscription_confirmed ? (
                <div
                  css={{
                    ...theme.mixins.fonts.golos,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 15,
                    marginBottom: 10,
                    padding: '24px 16px',
                    backgroundColor: theme.colors.white,
                    color: theme.colors.text,
                    height: 'auto',
                    alignItems: 'flex-start',
                    svg: {
                      paddingTop: 5,
                    },
                  }}
                >
                  <CheckMarkSVG css={{ width: 24, minWidth: 24 }} />
                  <span>
                    Мы сообщим вам, когда билеты на событие поступят в продажу
                  </span>
                </div>
              ) : (
                <div
                  css={{
                    ...theme.mixins.fonts.golos,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 15,
                    height: 180,
                    marginBottom: 10,
                    color: theme.colors.white,
                    padding: '24px 16px',
                    backgroundColor: theme.colors.red,
                  }}
                >
                  <p>
                    Оставьте e-mail, чтобы мы сообщили, когда билеты снова
                    появятся в продаже
                  </p>
                  <SubscribeForm
                    source='ArchiveSubscription'
                    eventId={eventData.id}
                    currentCity={currentCity}
                    onSuccessSubscribe={onSuccessSubscribe}
                  />
                </div>
              ))}
            <div
              css={{
                margin: '25px 0',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                fontFamily: 'Golos',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '16px',
                color: '#7D7B7A',
                textAlign: 'left',
              }}
            >
              {topTicketPage && (
                <div css={{ display: 'flex', gap: 10 }}>
                  <GuaranteeSVG css={{ width: 24 }} />
                  <BestPriceCollapsable
                    head={
                      <p css={{ cursor: 'pointer' }}>гарантия лучшей цены</p>
                    }
                    body={
                      <div>
                        если вы найдёте цену ниже, мы вернём разницу.{' '}
                        <button
                          css={{
                            fontFamily: 'Golos',
                            fontSize: '14',
                            fontWeight: 500,
                            lineHeight: '20px',
                            marginBottom: 10,
                            display: 'inline-block',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dashed',
                            cursor: 'pointer',
                            color: theme.colors.red,
                            ':hover': {
                              color: theme.colors.red,
                              textDecorationStyle: 'solid',
                            },
                          }}
                          type='button'
                          onClick={openBestPriceDecription}
                        >
                          Подробнее
                        </button>
                      </div>
                    }
                  />
                </div>
              )}
              {eventData.service_fee === 0 && (
                <div css={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <ServiceFreeSVG css={{ width: 24 }} />
                  <p>без сервисного сбора</p>
                </div>
              )}
              {eventData.service_fee && eventData.service_fee > 0 ? (
                <div css={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div css={{ position: 'relative' }}>
                    <CircleSVG css={{ width: 24 }} />
                    <div
                      css={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        fontFamily: 'Montserrat',
                        fontSize: 10,
                        fontWeight: 500,
                        color: theme.colors.red,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <p>{eventData.service_fee}%</p>
                    </div>
                  </div>
                  <p>сервисный сбор</p>
                </div>
              ) : (
                ''
              )}
              {['purchase', 'certificate', 'tsnext_widget'].includes(
                eventData.type
              ) && (
                <div css={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <LockSVG css={{ width: 24, minWidth: 24 }} />
                  <p>защита персональных данных</p>
                </div>
              )}
              {['purchase', 'certificate', 'tsnext_widget'].includes(
                eventData.type
              ) && (
                <div css={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <BankCardSVG css={{ width: 24, minWidth: 24 }} />
                  <p>безопасные платежи картой любого банка</p>
                </div>
              )}
              {eventData?.cancellation_policy && (
                <div
                  css={{
                    display: 'flex',
                    gap: 10,
                    alignItems:
                      eventData.cancellation_policy === '10_days'
                        ? 'flex-start'
                        : 'center',
                  }}
                >
                  <CancelSVG css={{ width: 24, minWidth: 24 }} />
                  <p>
                    {getCancellationPolicyText(eventData.cancellation_policy)}
                  </p>
                </div>
              )}
              {eventData.type === 'reservation' &&
                eventData.payment_on_spot && (
                  <div css={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <img
                      src={paymentOnSpotImg.src}
                      alt='бесплатное бронирование'
                      css={{ width: 24, minWidth: 24 }}
                    />
                    <p>бесплатное бронирование</p>
                  </div>
                )}
            </div>
          </div>
          <div
            className='col-12 col-md-6 push-md-1 col-lg-7 push-lg-1'
            css={{
              position: 'relative',
              left: '8.33333333%',
              [DEVICE_SCREENS_MAX_STRING.md]: {
                left: 0,
              },
            }}
          >
            <div css={{ marginBottom: 15 }}>
              <ShareBlock item={eventData} />
            </div>
            <div
              css={{
                marginBottom: 40,
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  '.crop-text': { margin: '0 8px 40px 8px' },
                },
              }}
              id='actions__description-block'
            >
              <Description
                title='Описание'
                description={eventData.description}
              />
              <div
                css={{ display: 'none' }}
                itemProp='description'
                dangerouslySetInnerHTML={{
                  __html: eventData.short_description,
                }}
              />
            </div>
            {eventData.block_descriptions?.map((block, index) => (
              <div
                css={{ marginBottom: 40 }}
                // eslint-disable-next-line react/no-array-index-key
                key={`description_block-${index}`}
              >
                <Description
                  title={block.block_title}
                  description={block.block_description_html}
                />
              </div>
            ))}
            {(eventData.images.length > 1 || eventData.video_url) && (
              <ImagesSlider
                videoInfo={{
                  video: eventData.video_url,
                  cover: eventData.video_cover,
                }}
                images={eventData.images}
                onSlideClick={(index: number) =>
                  setImageModal({ slide: index, visible: true })
                }
              />
            )}
            {eventData.condition_title && (
              <div css={{ marginBottom: 40 }}>
                <Description
                  title={eventData.condition_title}
                  description={eventData.condition}
                />
              </div>
            )}
            {!!socials?.results.length && (
              <div css={{ marginBottom: 36 }}>
                <AskQuestion socials={socials} />
              </div>
            )}
            <PersonsBlock eventData={eventData} />
            {isShowBuyButton && !eventData.date_unknown && (
              <div
                css={{
                  display: 'none',
                  [DEVICE_SCREENS_MAX_STRING.sm]: {
                    display: 'block',
                  },
                  marginBottom: 0,
                  position: 'fixed',
                  bottom: showPopUp ? 76 : 24,
                  left: 32,
                  right: 32,
                  zIndex: 100,
                  padding: 0,
                  button: {
                    borderRadius: 30,
                    height: 50,
                    fontSize: 18,
                  },
                }}
              >
                <BuyButton
                  callback={() => onClickBuyButton()}
                  disabled={isBuyDisabled || !isBuyButtonReady}
                  full
                  subscription_confirmed={
                    eventData.status === activeStatus &&
                    eventData.subscription_confirmed
                  }
                  {...(tsNextWidgetConfig &&
                    !(
                      isBuyDisabled ||
                      eventData.not_season ||
                      !isBuyButtonReady
                    ) && {
                      'data-type': tsNextWidgetConfig.type,
                      'data-uid': tsNextWidgetConfig.id,
                      'data-category': eventData.marketing_category,
                      'data-EXT_ID': auth.user?.external_ids.ext_id,
                      'data-ANALYTICS_ID':
                        auth.user?.external_ids.analytical_id,
                      className: 'tsnext-widget',
                    })}
                >
                  {eventData.status === activeStatus &&
                    eventData.subscription_confirmed && (
                      <CheckMarkSVG css={{ width: 16, height: 14 }} />
                    )}
                  {getBuyButtonText(eventData, isRanOutOfTickets)}
                </BuyButton>
              </div>
            )}
            <div>
              <CommentsBlock
                contentType='events.event'
                eventId={eventData.id}
                currentCity={currentCity}
              />
            </div>
          </div>
        </div>
        <div
          css={{
            '.actions__item': {
              width: 320,
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              '.actions__item': {
                width: 270,
              },
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              padding: '0 8px',
              '.actions__item': {
                maxWidth: '77%',
                width: '100%',
              },
            },
          }}
        >
          <SimilarEventsBlock eventData={eventData} currentCity={currentCity} />
          {categoriesBlockItems.length > 0 && (
            <CategoriesBlock
              items={categoriesBlockItems}
              currentCity={currentCity}
            />
          )}
          <Viewed title='Вы смотрели' viewed={viewed} />
        </div>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: '4rem',
            marginTop: '2rem',
            [DEVICE_SCREENS_MAX_STRING.xl]: {
              gridTemplateColumns: '1fr',
              padding: '0 8px',
            },
          }}
        >
          <Subscription currentCity={currentCity} />
          <MobileApps />
        </div>
        <UpButton elementId='actions__description-block' screenCount={1.5} />
        {!!imageModal.visible &&
          (!!eventData.images?.length || !!eventData.video_url) && (
            <PictureModal
              videoInfo={{
                video: eventData.video_url,
                cover: eventData.video_cover,
              }}
              images={eventData.images.slice(1)}
              imageModal={imageModal}
              scrollY={window.scrollY}
              setVisible={setImageModal}
            />
          )}
      </main>
      <TSNextWidgetScript />
    </Container>
  );
}

export const PageComponent = withSuspense(PageComponentInner);
